import React from 'react';

import { ActivityStage, useRoomParticipants } from '@interactive-room';

import type { GameMode, ParticipantRole, RoomPhase } from '@interactive-room';

import { MODE_VISUALS } from '../../theme/designSystem';

import { PodiumStage } from './PodiumStage';

interface ActivityFrameProps {
  role: ParticipantRole;
  phase: RoomPhase;
  mode?: GameMode | null;
  readOnly?: boolean;
  fullscreen?: boolean;
  /** Superficie clara para vista Campo (estudiante) */
  surface?: 'stage' | 'field';
}

export function ActivityFrame({
  role,
  phase,
  mode,
  readOnly,
  fullscreen,
  surface = 'stage',
}: ActivityFrameProps) {
  const visual = mode ? MODE_VISUALS[mode] : null;
  const isField = surface === 'field';
  const isCompact = fullscreen && !isField;
  const { participants } = useRoomParticipants();

  if (phase === 'LOBBY' || phase === 'ENDED') {
    return (
      <div
        className={`sala-glass-raised flex flex-col items-center justify-center text-center ${
          fullscreen ? 'h-full min-h-0' : 'min-h-[200px] py-16'
        }`}
      >
        <div
          className="mb-6 h-16 w-16 rounded-2xl border-[1.5px] border-slate-950 shadow-[3px_3px_0_0_#020617]"
          style={{
            background: `conic-gradient(from 180deg, var(--sala-electric-purple), var(--sala-acid-mint), var(--sala-electric-purple))`,
          }}
        />
        <p className="font-display text-3xl font-black uppercase tracking-tight text-[var(--sala-ink)] md:text-4xl">
          {phase === 'LOBBY' ? 'Escenario listo' : 'Sesión finalizada'}
        </p>
        <p className="mt-3 max-w-md text-sm font-medium text-[var(--sala-ink-dim)]">
          {phase === 'LOBBY'
            ? 'Selecciona un modo y lanza la actividad. Aquí aparecerá la vista en vivo.'
            : 'Gracias por usar Edusico Sala.'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden ${fullscreen ? 'h-full min-h-0' : ''}`}
      style={
        visual
          ? {
              ['--sala-accent' as string]: visual.hue,
              ['--sala-accent-neon' as string]: visual.neon,
              ['--sala-accent-glow' as string]: `${visual.neon}55`,
            }
          : undefined
      }
    >
      <div className="absolute inset-0 rounded-2xl opacity-20" style={{ background: visual?.mesh }} />
      <div
        className={`relative flex h-full min-h-0 flex-col overflow-hidden rounded-2xl ${
          isField ? 'sala-stage-field' : 'sala-stage-dark border-[1.5px] border-slate-950'
        }`}
        style={{
          ...(isField ? { background: 'var(--sala-surface-pure, #fff)' } : {}),
          minHeight: fullscreen ? undefined : 'min(420px, 55vh)',
        }}
      >
        <div
          className={`absolute inset-x-0 top-0 z-10 flex shrink-0 items-center justify-between ${
            isCompact ? 'px-3 py-2' : 'px-5 py-3'
          }`}
          style={{ borderBottom: '1.5px solid #020617' }}
        >
          <span className="sala-label font-black uppercase tracking-widest">Escenario en vivo</span>
          {visual && (
            <span
              className="font-display text-xs font-black uppercase tracking-widest"
              style={{ color: visual.neon }}
            >
              {visual.label}
            </span>
          )}
        </div>

        <div
          className={`sala-stage-inner flex min-h-0 flex-1 flex-col ${
            isCompact ? 'sala-stage-inner--compact p-2 pt-9 sm:p-3 sm:pt-10' : 'p-6 pt-14 sm:p-10 sm:pt-16'
          }`}
        >
          <div className={`flex min-h-0 flex-1 flex-col ${isCompact ? 'h-full overflow-hidden' : 'justify-center'}`}>
            {phase === 'PODIUM' ? (
              <PodiumStage participants={participants} size={fullscreen ? 'lg' : 'md'} />
            ) : (
              <ActivityStage role={role} readOnly={readOnly} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
